import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { GuidedBuilderConfig, GuidedBuilderStep } from '@/types/guidedBuilder';

const defaultConfig: GuidedBuilderConfig = {
  enabled: true,
  steps: [],
};

export const useGuidedBuilderConfig = () => {
  const [config, setConfig] = useState<GuidedBuilderConfig>(defaultConfig);
  const [isLoading, setIsLoading] = useState(true);

  const fetchConfig = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('global_settings')
        .select('setting_value')
        .eq('setting_key', 'guided_builder_config')
        .eq('is_active', true)
        .single();

      if (error) throw error;

      if (data?.setting_value) {
        const value = data.setting_value as unknown as GuidedBuilderConfig;
        // Sort steps by order
        if (value.steps) {
          value.steps.sort((a, b) => a.order - b.order);
        }
        setConfig(value);
      }
    } catch (err) {
      console.error('Error fetching guided builder config:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const updateConfig = async (newConfig: GuidedBuilderConfig) => {
    try {
      const { error } = await supabase
        .from('global_settings')
        .update({ setting_value: newConfig as unknown as Record<string, never> })
        .eq('setting_key', 'guided_builder_config');

      if (error) throw error;

      setConfig(newConfig);
      return true;
    } catch (err) {
      console.error('Error updating guided builder config:', err);
      return false;
    }
  };

  const updateStep = async (stepId: string, updates: Partial<GuidedBuilderStep>) => {
    const newSteps = config.steps.map(step =>
      step.id === stepId ? { ...step, ...updates } : step
    );
    return updateConfig({ ...config, steps: newSteps });
  };

  const addStep = async (step: GuidedBuilderStep) => {
    const newSteps = [...config.steps, step].sort((a, b) => a.order - b.order);
    return updateConfig({ ...config, steps: newSteps });
  };

  const deleteStep = async (stepId: string) => {
    const newSteps = config.steps.filter(step => step.id !== stepId);
    // Reorder remaining steps
    newSteps.forEach((step, index) => {
      step.order = index + 1;
    });
    return updateConfig({ ...config, steps: newSteps });
  };

  const reorderSteps = async (stepIds: string[]) => {
    const newSteps = stepIds.map((id, index) => {
      const step = config.steps.find(s => s.id === id);
      if (step) {
        return { ...step, order: index + 1 };
      }
      return null;
    }).filter(Boolean) as GuidedBuilderStep[];
    return updateConfig({ ...config, steps: newSteps });
  };

  const toggleEnabled = async () => {
    return updateConfig({ ...config, enabled: !config.enabled });
  };

  return {
    config,
    isLoading,
    refetch: fetchConfig,
    updateConfig,
    updateStep,
    addStep,
    deleteStep,
    reorderSteps,
    toggleEnabled,
  };
};
